import { storageMap } from "components/organisms/CitySection/storageMap";

export const storageUnitsNearMe = {
	menuName: "Storage Units Near Me",
	sections: [
		{
			title: "Amenities",
			defaultExpanded: false,
			links: [
				{
					text: "24 Hour Storage Units Near You",
					href: "/storage-units-near-me/24-hour-storage-units/"
				},
				{
					text: "Climate Controlled Storage Units Near You",
					href: "/storage-units-near-me/climate-controlled-storage-units/"
				},
				{
					text: "Drive Up Storage Units Near You",
					href: "/storage-units-near-me/drive-up-storage-units/"
				},
				{
					text: "Storage Units with Electricity Near You",
					href: "/storage-units-near-me/electricity-storage-units/"
				},
				{
					text: "Storage Units with Free Truck Rental Near You",
					href: "/storage-units-near-me/storage-units-with-free-truck-rental/"
				},
				{
					text: "Wine Storage Units Near You",
					href: "/storage-units-near-me/wine-storage-units/"
				}
			]
		},
		{
			title: "Unit Sizes",
			defaultExpanded: false,
			links: [
				{
					text: "Mini Storage Units Near You",
					href: "/storage-units-near-me/mini-storage-units/"
				},
				{
					text: "5x5 Storage Units Near You",
					href: "/storage-units-near-me/5x5-storage-units/"
				},
				{
					text: "5x10 Storage Units Near You",
					href: "/storage-units-near-me/5x10-storage-units/"
				},
				{
					text: "5x15 Storage Units Near You",
					href: "/storage-units-near-me/5x15-storage-units/"
				},
				{
					text: "10x10 Storage Units Near You",
					href: "/storage-units-near-me/10x10-storage-units/"
				},
				{
					text: "10x15 Storage Units Near You",
					href: "/storage-units-near-me/10x15-storage-units/"
				},
				{
					text: "10x20 Storage Units Near You",
					href: "/storage-units-near-me/10x20-storage-units/"
				},
				{
					text: "10x25 Storage Units Near You",
					href: "/storage-units-near-me/10x25-storage-units/"
				},
				{
					text: "10x30 Storage Units Near You",
					href: "/storage-units-near-me/10x30-storage-units/"
				},
				{
					text: "Storage Locker Units Near You",
					href: "/storage-units-near-me/locker-storage-units/"
				}
			]
		},
		{
			title: "Vehicles",
			defaultExpanded: false,
			links: [
				{
					text: "Boat Storage Units Near You",
					href: "/storage-units-near-me/boat/"
				},
				{
					text: "Car Storage Units Near You",
					href: "/storage-units-near-me/car/"
				},
				{
					text: "RV Storage Units Near You",
					href: "/storage-units-near-me/rv/"
				}
			]
		}
	]
};

// The API returns "{city}, {state} {category}"; anchor text needs "{category} in {city}, {state}".
const DRILLDOWN_TEXT_PATTERN = /^(.+?),\s*([A-Za-z]{2})\s+(.+)$/;

// Normalizes the API's inconsistent category wording against CitySection's canonical map.
function canonicalizeCategory(category) {
	const matched = storageMap.find((t) =>
		category?.toLowerCase().includes(t.key.toLowerCase())
	);
	return matched ? matched.label : category;
}

function localizeDrilldownText(text) {
	const match = text?.match(DRILLDOWN_TEXT_PATTERN);
	if (!match) return text;
	const [, city, state, category] = match;
	return `${canonicalizeCategory(category)} in ${city}, ${state}`;
}

// On CLP/amenity pages, the menu is built straight from the API's own city-scoped drilldownLinks.
const DRILLDOWN_SECTIONS = [
	{
		key: "amenities",
		title: "Storage Units Near You by Amenity",
		buildTitle: (city) => `Storage Units with Amenities in ${city}`
	},
	{
		key: "unitTypes",
		title: "Storage Units Near You by Size",
		buildTitle: (city) => `Storage Units by Size in ${city}`
	},
	{
		key: "vehicleStorage",
		title: "Storage Units for Vehicles Near You",
		buildTitle: (city) => `Storage Units for Vehicles in ${city}`
	}
];

// Pulled from the drilldownLinks payload itself rather than threaded in as a prop.
function extractCity(drilldownLinks) {
	const match = DRILLDOWN_SECTIONS.map(({ key }) =>
		drilldownLinks[key]?.[0]?.text?.match(DRILLDOWN_TEXT_PATTERN)
	).find(Boolean);
	if (!match) return null;
	const [, city] = match;
	return city;
}

function buildSectionsFromDrilldownLinks(drilldownLinks) {
	const city = extractCity(drilldownLinks);

	return DRILLDOWN_SECTIONS.map(({ key, title, buildTitle }) => ({
		title: city ? buildTitle(city) : title,
		defaultExpanded: false,
		links: (drilldownLinks[key] || []).map((item) => ({
			text: localizeDrilldownText(item.text),
			href: `/${item.uri}`
		}))
	})).filter((section) => section.links.length > 0);
}

// Text can only be localized to a city when the href is too (via drilldownLinks) -
// otherwise the anchor names a city the near-me href doesn't actually resolve to.
export function getStorageUnitsNearMe(drilldownLinks) {
	if (drilldownLinks) {
		const sections = buildSectionsFromDrilldownLinks(drilldownLinks);
		// Fall back to the generic near-me list rather than render an empty menu.
		if (sections.length > 0) {
			return { ...storageUnitsNearMe, sections };
		}
	}

	return storageUnitsNearMe;
}

export const dealsLink = {
	text: "Deals",
	href: "/storage-units-near-me/deals/"
};

export const storage101 = {
	menuName: "Storage 101",
	links: [
		{
			text: "Size Guide",
			href: "/self-storage-size-guide/"
		},
		{
			text: "Indoor vs. Outdoor Storage",
			href: "/indoor-vs-outdoor/"
		},
		{
			text: "Storage Security",
			href: "/storage-security/"
		},
		{
			text: "Storage Insurance",
			href: "/storage-insurance/"
		}
	]
};

export const storageTypes = {
	menuName: "Resources",
	links: [
		{
			text: "Blog",
			href: "https://www.storage.com/blog/"
		},
		{
			text: "Storage Unit Size Guide",
			href: "/self-storage-size-guide/"
		},
		{
			text: "Storage Unit Amenities 101",
			submenu: [
				{
					text: "24-Hour Access Storage Units",
					href: "/storage-solutions/24-hour-storage-units/"
				},
				{
					text: "Climate-Controlled Storage Units",
					href: "/storage-solutions/climate-controlled-storage-units/"
				},
				{
					text: "Drive-Up Access Storage Units",
					href: "/storage-solutions/drive-up-storage-units/"
				},
				{
					text: "Portable Storage Units",
					href: "/storage-solutions/portable-storage-units/"
				},
				{
					text: "Storage Units With Electricity",
					href: "/storage-solutions/storage-units-with-electricity/"
				},
				{
					text: "Storage Units with Free Truck Rental",
					href: "/storage-solutions/storage-units-with-free-truck-rental/"
				},
				{
					text: "Wine Storage Units",
					href: "/storage-solutions/wine-storage-units/"
				}
			]
		},
		{
			text: "Storage Unit Solutions 101",
			submenu: [
				{
					text: "College Storage Units",
					href: "/storage-solutions/college-storage-units/"
				},
				{
					text: "Business Storage Units",
					href: "/storage-solutions/business-storage-units/"
				},
				{
					text: "Military Storage Units",
					href: "/storage-solutions/military-storage-units/"
				}
			]
		},
		{
			text: "Vehicle Storage Units 101",
			submenu: [
				{
					text: "ATV Storage Units",
					href: "/storage-solutions/atv-storage-units/"
				},
				{
					text: "Bicycle Storage Units",
					href: "/storage-solutions/bike-storage-units/"
				},
				{
					text: "Boat Storage Units",
					href: "/storage-solutions/boat-storage-units/"
				},
				{
					text: "Car Storage Units",
					href: "/storage-solutions/car-storage-units/"
				},
				{
					text: "Dirt Bike Storage Units",
					href: "/storage-solutions/dirt-bike-storage-units/"
				},
				{
					text: "Golf Cart Storage Units",
					href: "/storage-solutions/golf-cart-storage-units/"
				},
				{
					text: "Jet Ski Storage Units",
					href: "/storage-solutions/jet-ski-storage-units/"
				},
				{
					text: "Kayak Storage Units",
					href: "/storage-solutions/kayak-storage-units/"
				},
				{
					text: "Motorcycle Storage Units",
					href: "/storage-solutions/motorcycle-storage-units/"
				},
				{
					text: "RV Storage Units",
					href: "/storage-solutions/rv-storage-units/"
				},
				{
					text: "Vehicle Storage Units",
					href: "/storage-solutions/vehicle-storage-units/"
				}
			]
		}
	]
};
